import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import SnackStore from '../../stores/SnackStore';
import CategoriesSectionStore from '../../stores/Policy/CategoriesSectionStore';
import { ModalMode } from '../../stores/Policy/PolicyModalStore';
import PolicyStore from '../../stores/Policy/PolicyStore';
import FuseConsoleCardFooter from '../ConsoleFragments/FuseConsoleCardFooter';
import { Card, FTBody, FTLabel } from '../Fuse';
import MatchCriteriaStore from '../../stores/Policy/MatchCriteriaStore';
import FuseReadOnlyCheckbox from '../FuseControls/FuseReadOnlyCheckbox';
import FuseReadOnlySelect from '../FuseControls/FuseReadOnlySelect';
import FuseButton from '../FuseControls/FuseButton';
import CheckboxContainer from '../CheckboxContainer/CheckboxContainer';
import { And } from './ConnectionBlock/ConnectionBlock';
import CategoriesSection from './CategoriesSection/CategoriesSection';
import PolicySection from './PolicySection';
import PolicySectionHeader from './PolicySectionHeader/PolicySectionHeader';
import FuseSelect from '../FuseControls/FuseSelect';
import Icon from '../Icon/Icon';
import './MatchCriteria.css';

const block = b.lock('MatchCriteria');

const MANY_ITEMS_COUNT = 3;

class MatchCriteria extends Component {
    static propTypes = {
        store: MatchCriteriaStore.PropType.isRequired,
        categoriesStore: CategoriesSectionStore.PropType,
        readOnly: PropTypes.bool,
        policy: PolicyStore.PropType.isRequired,
        footerText: PropTypes.string,
        snack: SnackStore.PropType.isRequired,
        sectionsInfo: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.oneOf(['Browsing Roles', 'Isolation Channels', 'Categories']),
            extraSection: PropTypes.func,
            extraSectionName: PropTypes.string,
            isEmpty: PropTypes.func,
            canHide: PropTypes.bool,
        })),
    };

    static defaultProps = {
        sectionsInfo: [{ name: 'Browsing Roles' }, { name: 'Isolation Channels' }, { name: 'Categories' }]
    }


    constructor(props) {
        super(props);
        const { store, categoriesStore, snack, sectionsInfo } = this.props;
        const notifyForFailure = (entity) => () => snack.showError(<span>Failed to load <b>{entity}</b></span>);
        categoriesStore.load();
        store.loadGroups().catch(notifyForFailure('User Groups'));
        store.loadRoles().catch(notifyForFailure('Browsing Roles'));
        store.setIsoChannelBasedOnLicense();

        if (store.visibleSections.length === 0) {
            const visibleSections = [];
            const hiddenSections = [];
            sectionsInfo.forEach((section) => {
                if (section.canHide && section.isEmpty?.() && sectionsInfo.length >= MANY_ITEMS_COUNT) {
                    hiddenSections.push(section);
                } else {
                    visibleSections.push(section);
                }
            });
            store.setVisibleHiddenSections(visibleSections, hiddenSections);
        }
    }

    onShowHiddenSection = (hiddenSectionIndex) => {
        const { store } = this.props;
        const section = store.hiddenSections[hiddenSectionIndex];
        store.hiddenSections.splice(hiddenSectionIndex, 1);
        store.setVisibleHiddenSections([...store.visibleSections, section], [...store.hiddenSections]);
    }

    onCreateBrowsingRoleClick = () => {
        const { store, policy } = this.props;

        policy.browsingRoles.policyTable.openModal(ModalMode.CREATE, null, (item) => {
            store.loadRoles()
                .then(() => store.selectBrowsingRole(item));
        });
    }

    renderBrowsingRolesSection = (store, policy, policyProps, readOnly) => {
        return <PolicySection {...policyProps}>
            <PolicySectionHeader
                title="Browsing Roles"
                tooltipText={`Select the browsing roles you would like to be included in this match criteria.
                    Disabled Roles will not be shown. You can also select Match All to match any role ever defined.`}
            />

            {(readOnly && store.matchAllRoles.isTrue) ?
                <FTBody>
                    {'All Users'}
                </FTBody> :
                <Fragment>
                    {!readOnly ?
                        <CheckboxContainer
                            className={block('BrowsingSubHeader')}
                            readOnly={readOnly}
                            matchAll={store.matchAllRoles.isTrue}
                        >
                            <FuseReadOnlyCheckbox
                                store={store.matchAllRoles}
                                label="Match All Users"
                                qa="match-all-roles"
                            />
                            <FuseButton
                                type="tertiary"
                                icon="add_circle"
                                wrapperClassName={block('AddButtonWrapper')}
                                color="blue"
                                onClick={this.onCreateBrowsingRoleClick}
                            >New Browsing Role</FuseButton>
                        </CheckboxContainer>
                        : null
                    }
                    {!store.matchAllRoles.isTrue ?
                        <Fragment>
                            <FuseReadOnlySelect
                                containerClassName={block('BrowsingRolesSelect')}
                                readOnly={readOnly}
                                store={store.browsingRolesSelect}
                                loading={policy.browsingRoles.policyTable.listStatus.isLoading}
                                selectAllEnabled
                                fullWidth
                                searchable
                                noOptionsLabel="No entries found"
                                searchLabel="Search by Name, User Group, or Email"
                                placeholder="Select Browsing Roles"
                                qa="browsing-roles"
                            />
                            {!readOnly && (store.referencesDisabledRoles() || store.referencesUnSyncedGroups()) ?
                                <FTLabel className={block('DisabledLegend', { readOnly })}>
                                    Browsing Roles displayed in red have been disabled or contain a group that is
                                    not available in the source system for synchronization.
                                </FTLabel> : null
                            }
                        </Fragment>
                        : null
                    }
                    {!readOnly ? <FuseConsoleCardFooter
                        className={block('SectionFooter')}
                        text={'At least one Browsing Role is required to be selected.'}
                    /> : null}
                </Fragment>
            }
        </PolicySection>;
    }

    renderIsolationChannelsSection = (store, policy, policyProps, readOnly) => {
        return <PolicySection {...policyProps}>
            <PolicySectionHeader
                title="Isolation Channels"
                tooltipText={`Select the browsing channels you would like to be included in this match criteria.
                    A Channel is the pathway, or product, used to redirect traffic into Isolation.`}
            />

            {store.isDefault || (readOnly && store.isAllChannelsSelected())
                ? <FTBody>All Channels</FTBody>
                : <Fragment>
                    <FuseReadOnlySelect
                        readOnly={readOnly}
                        store={store.isolationChannelsSelect}
                        selectAllEnabled
                        fullWidth
                        placeholder={readOnly ? 'No Channels Chosen' : 'Select Isolation Channels'}
                        qa="channels"
                    />

                    {!readOnly && store.referencesInactiveChannels() ?
                        <FTLabel className={block('DisabledLegend', { readOnly })}>
                            Your current Isolation license does not support channels displayed in red.
                        </FTLabel> : null
                    }

                    {!readOnly ? <FuseConsoleCardFooter
                        className={block('SectionFooter')}
                        text={'At least one Isolation Channel is required to be selected.'}
                    /> : null}
                </Fragment>
            }
        </PolicySection>;
    }

    onRemoveVisibleSection = (visibleSectionIndex) => {
        const { store } = this.props;
        const section = store.visibleSections[visibleSectionIndex];
        section.clear?.();
        store.visibleSections.splice(visibleSectionIndex, 1);
        store.setVisibleHiddenSections([...store.visibleSections], [...store.hiddenSections, section]);
    }

    renderSection = (section, sectionIndex, readOnly, sectionElement) => {
        if (readOnly && section.canHide && section.isEmpty?.()) {
            return null;
        }
        return <div className={block('SectionContainer')}>
            {section.canHide && !readOnly ? <FuseButton
                className={block('RemoveSectionButton')}
                type={'tertiary'}
                color={'blue'}
                icon={'delete'}
                onClick={() => this.onRemoveVisibleSection(sectionIndex)}
            >Remove</FuseButton> : null}
            {sectionElement}
        </div>;
    }

    render() {
        const { store, policy, sectionsInfo, footerText } = this.props;
        let { readOnly } = this.props;
        const { visibleSections, hiddenSections } = store;
        const policyProps = {
            grayBackground: !readOnly,
            readOnly
        };
        const showHiddenSectionsSelect = sectionsInfo.some((section) => section.canHide);
        readOnly = readOnly || store.isDefault;

        return <Card title="Match Criteria">
            {(readOnly ? sectionsInfo : visibleSections)
                .map((section, sectionIndex) => {
                    if (!section.name) {
                        return this.renderSection(section, sectionIndex, readOnly, section.extraSection(readOnly));
                    }
                    if (section.name === 'Browsing Roles') {
                        return this.renderSection(section, sectionIndex, readOnly,
                            this.renderBrowsingRolesSection(store, policy, policyProps, readOnly));
                    }
                    if (section.name === 'Isolation Channels') {
                        return this.renderSection(section, sectionIndex, readOnly,
                            this.renderIsolationChannelsSection(store, policy, policyProps, readOnly));
                    }
                    if (section.name === 'Categories') {
                        return this.renderSection(section, sectionIndex, readOnly,
                            <CategoriesSection
                                store={this.props.categoriesStore}
                                readOnly={readOnly}
                            />);
                    }
                    return null;
                })
                .filter((section) => !!section)
                .reduce((sections, currentSection) => ([
                    ...sections,
                    sections.length === 0 || readOnly ? null : <And />,
                    currentSection,
                ]), [])
                .map((element, index) => <Fragment key={'element-' + index}>{element}</Fragment>)}

            {!readOnly && showHiddenSectionsSelect ? <FuseSelect
                singleSelection
                actionStyle
                containerClassName={block('HiddenSectionsSelect')}
                disabled={hiddenSections.length === 0}
                onSelect={this.onShowHiddenSection}
                actionStylePlaceHolder={<span className={block('CriteriaPlaceHolder')}>
                    <Icon
                        className={block('CriteriaPlaceHolderIcon')}
                        type={'add'}
                    />{'Match Criteria'}
                </span>}
                options={hiddenSections.map((section, sectionIndex) => ({
                    value: sectionIndex,
                    label: section.name || section.extraSectionName
                }))}
                qa="match-criteria"
            /> : null}

            {store.isDefault ? <FuseConsoleCardFooter
                className={block('SectionFooter')}
                text={footerText || 'Match Criteria for the Default Rule includes all Browsing Roles, websites, ' +
                'and Isolation Channels, and cannot be changed.'}
            /> : null}
        </Card>;
    }
}

export default inject('policy', 'snack')(observer(MatchCriteria));
